import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import './ChangePassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });

    // If the recovery event hasn't fired after 8 seconds, the link is likely invalid
    const timeout = setTimeout(() => {
      setExpired((prev) => {
        if (!prev) return true;
        return prev;
      });
    }, 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Cancel the expired state if ready fires
  useEffect(() => {
    if (ready) setExpired(false);
  }, [ready]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updateError) {
      setError(updateError.message || 'Failed to update password.');
    } else {
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    }
  };

  if (done) {
    return (
      <div className="change-password-page">
        <div className="change-password-container">
          <div className="change-password-header">
            <h1>Password updated</h1>
            <p>Your password has been updated. Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  if (expired && !ready) {
    return (
      <div className="change-password-page">
        <div className="change-password-container">
          <div className="change-password-header">
            <h1>Link invalid or expired</h1>
            <p>This password reset link is invalid or has expired. Please ask your administrator to send a new one.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="change-password-page">
        <div className="change-password-container">
          <div className="change-password-header">
            <h1>Reset password</h1>
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="change-password-header">
          <h1>Reset password</h1>
          <p>Enter your new password below.</p>
        </div>
        <form onSubmit={handleSubmit} className="change-password-form">
          {error && <div className="change-password-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="change-password-submit" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
